import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, type Resolver } from "react-hook-form";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { useToast } from "../hooks/useToast";
import { Save, Edit, Trash2, Loader2, Info, ArrowLeft, Check, TrendingUp } from "lucide-react";
import { apiRequest } from "../components/lib/queryClient";
import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence, motion } from "framer-motion";
import { Separator } from "../components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "../components/ui/tooltip";
import { useAuth } from "../components/features/AuthContext";
import { ApiResponse } from "../components/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { CryptoPair, cryptoPairSchema, FilterApiSchema, StockObject, StockObjectSchema } from "./LeaderAssetManagement";


const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.05, duration: 0.3 }
  })
};

const orderSettingsSchema = z.object({
  id: z.string().optional(),
  userId: z.string().optional(),
  settingType: z.enum(["private", "common"]).optional(),
  category: z.enum(["stocks", "crypto"]),
  name: z.string().min(1, "Name is required"),
  stopLoss: z.string().optional(),
  target: z.string().optional(),
  trailingStopLoss: z.string().optional(),
  orderType: z.enum(["market", "limit", "stop-limit"]),
  limit: z.string().optional(),
  checkBenchmark: z.boolean(),
  sentimentAnalysis: z.boolean(),
  orderTimeout: z.string().min(1, "Order timeout is required"),
  autoIncreament: z.boolean(),
  strikeDiff: z.enum(["-5", "-4", "-3", "-2", "-1", "0", "1", "2", "3", "4", "5"]),
  maxCapitalUsage: z.enum([
    "50", "55", "60", "65", "70", "75", "80", "85", "90", "95", "100",
  ]).optional(),
  orderCategory: z.enum(["normal", "derivative"]),
  tradeCooldown: z.enum([
    "0", "15", "30", "45", "60", "75", "90", "105", "120"
  ]).optional(),
  tradeType: z.enum(["delivery", "intraday"]).optional(),
  leverage: z.string().optional(),
  maxOrderAmount: z.string().optional(),
  useLeader: z.boolean().optional(),
  leader: z.union([cryptoPairSchema, StockObjectSchema]).optional(),
  leaderStrategyIds: z.array(z.string()).optional(),
}).refine(
  (data) =>
    data.category === "stocks" ||
    (data.leverage !== undefined &&
      !isNaN(parseInt(data.leverage)) &&
      parseInt(data.leverage) >= 5 &&
      parseInt(data.leverage) <= 125),
  {
    message: "Leverage must be between 5x and 125x",
    path: ["leverage"],
  }
);

export type OrderSettingsFormData = z.infer<typeof orderSettingsSchema>;
export default function OrderSettings() {
  const [editingSettings, setEditingSettings] = useState<any>(null);
  const { toast } = useToast();
  const { currentTradingMode, user } = useAuth();
  const category = currentTradingMode;
  const queryClient = useQueryClient();


  const form = useForm<OrderSettingsFormData>({
    resolver: zodResolver(orderSettingsSchema) as Resolver<OrderSettingsFormData>,
    defaultValues: {
      name: "",
      stopLoss: "",
      target: "",
      category: currentTradingMode,
      trailingStopLoss: "",
      orderType: "market",
      limit: "",
      orderTimeout: "60",
      autoIncreament: false,
      checkBenchmark: false,
      sentimentAnalysis: false,
      strikeDiff: "0",
      maxCapitalUsage: "100",
      tradeCooldown: "0",
      orderCategory: "normal",
      tradeType: "delivery",
      settingType: "private",
      useLeader: false,
      leader: undefined,
      leaderStrategyIds: [],
    }
  });

  const useLeader = form.watch("useLeader");

  // Fetch Leaders
  const {
    data: leaders,
    isLoading: isLoadingLeaders,
    error: leadersError,
    refetch: refetchLeaders,
  } = useQuery<(StockObject | CryptoPair)[]>({
    queryKey: ["api/leaders", category],
    queryFn: async () => {
      const params = new URLSearchParams({ category });
      const res = await apiRequest<ApiResponse<(StockObject | CryptoPair)[]>>(
        "GET",
        `api/leaders?${params}`
      );
      return res.data;
    },
    enabled: !!category,
  });
  // Fetch Leader Strategies
  const {
    data: strategies,
    isLoading: isLoadingStrategies,
    error: strategiesError,
  } = useQuery<FilterApiSchema[]>({
    queryKey: ["api/strategies", category],
    queryFn: async () => {
      const params = new URLSearchParams({ category, type: "LEADER" });
      const res = await apiRequest<ApiResponse<FilterApiSchema[]>>(
        "GET",
        `api/strategies?${params}`
      );
      return res.data;
    },
    enabled: !!category,
  });

  const { data: orderSettings, isLoading } = useQuery({
    queryKey: ["api/order-settings", user?.userId, currentTradingMode],
    queryFn: async () => {
      if (!user?.userId || !user?.role || !currentTradingMode) return [];
      const api_url = `api/order-settings?userId=${user.userId}&role=${user.role}&category=${currentTradingMode}`;
      const res = await apiRequest<ApiResponse<OrderSettingsFormData[]>>("GET", api_url);
      return res.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data: OrderSettingsFormData) => {
      if (isNaN(parseInt(data.orderTimeout))) {
        throw new Error("Invalid order timeout value");
      }
      const payload: Record<string, any> = {
        userId: user?.userId,
        userRole: user?.role,
        name: data.name,
        stopLoss: data.stopLoss ? parseFloat(data.stopLoss) : undefined,
        target: data.target ? parseFloat(data.target) : undefined,
        trailingStopLoss: data.trailingStopLoss ? parseFloat(data.trailingStopLoss) : undefined,
        category: currentTradingMode,
        orderType: data.orderType,
        limit: data.limit ? parseFloat(data.limit) : undefined,
        orderTimeout: parseInt(data.orderTimeout),
        autoIncreament: data.autoIncreament,
        checkBenchmark: data.checkBenchmark,
        sentimentAnalysis: data.sentimentAnalysis,
        strikeDiff: parseInt(data.strikeDiff),
        tradeCooldown: data.tradeCooldown ? parseInt(data.tradeCooldown) : undefined,
        maxCapitalUsage: data.maxCapitalUsage ? parseInt(data.maxCapitalUsage) : undefined,
        orderCategory: data.orderCategory,
        tradeType: data.tradeType,
        settingType: data.settingType,
        leverage: data.leverage ? parseInt(data.leverage) : undefined,
        maxOrderAmount: data.maxOrderAmount ? parseFloat(data.maxOrderAmount) : undefined,
        useLeader: data.useLeader,
        leader: data.leader,
        leaderStrategyIds: data.leaderStrategyIds,
      };
      return await apiRequest("POST", "api/order-settings", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api/order-settings", user?.userId, currentTradingMode] });
      setEditingSettings(null);
      form.reset();
      toast({
        title: "Success",
        description: "Order settings updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save order settings",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: OrderSettingsFormData) => {
      if (isNaN(parseInt(data.orderTimeout))) {
        throw new Error("Invalid order timeout value");
      }
      const payload = {
        _id: data.id,
        userId: user?.userId,
        userRole: user?.role,
        name: data.name,
        stopLoss: data.stopLoss ? parseFloat(data.stopLoss) : undefined,
        target: data.target ? parseFloat(data.target) : undefined,
        trailingStopLoss: data.trailingStopLoss ? parseFloat(data.trailingStopLoss) : undefined,
        orderType: data.orderType,
        category: currentTradingMode,
        limit: data.limit ? parseFloat(data.limit) : undefined,
        orderTimeout: parseInt(data.orderTimeout),
        autoIncreament: data.autoIncreament,
        checkBenchmark: data.checkBenchmark,
        sentimentAnalysis: data.sentimentAnalysis,
        strikeDiff: parseInt(data.strikeDiff),
        tradeCooldown: data.tradeCooldown ? parseInt(data.tradeCooldown) : undefined,
        maxCapitalUsage: data.maxCapitalUsage ? parseInt(data.maxCapitalUsage) : undefined,
        orderCategory: data.orderCategory,
        tradeType: data.tradeType,
        settingType: data.settingType,
        leverage: data.leverage ? parseInt(data.leverage) : undefined,
        maxOrderAmount: data.maxOrderAmount ? parseFloat(data.maxOrderAmount) : undefined,
        useLeader: data.useLeader,
        leader: data.leader,
        leaderStrategyIds: data.leaderStrategyIds,
      };
      return await apiRequest<ApiResponse<OrderSettingsFormData>>("PUT", `api/order-settings`, payload);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["api/order-settings", user?.userId, currentTradingMode] });
      setEditingSettings(null);
      form.reset({});
      toast({
        title: "Success",
        description: data?.message || "Order settings updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update order settings",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!id) throw new Error("Missing configuration ID");
      const queryParams = new URLSearchParams({
        userId: user?.userId || "",
        userRole: user?.role || "",
      }).toString();
      return await apiRequest("DELETE", `api/order-settings/${id}?${queryParams}`);
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ["api/order-settings", user?.userId, currentTradingMode] });
      const previousSettings = queryClient.getQueryData(["api/order-settings", user?.userId, currentTradingMode]);
      queryClient.setQueryData(["api/order-settings", user?.userId, currentTradingMode], (old: any[] = []) =>
        old?.filter((item) => item.id !== id)
      );
      return { previousSettings };
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Order settings deleted successfully",
      });
    },
    onError: (error: Error, id, context) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete order settings",
        variant: "destructive",
      });
      if (context?.previousSettings) {
        queryClient.setQueryData(["api/order-settings", user?.userId, currentTradingMode], context.previousSettings);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["api/order-settings", user?.userId, currentTradingMode] });
    },
  });

  const onSubmit = (data: OrderSettingsFormData) => {
    if (editingSettings) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (settings: OrderSettingsFormData) => {
    setEditingSettings(settings);
    form.reset({
      id: settings.id ?? undefined,
      userId: settings.userId ?? undefined,
      settingType: settings.settingType ?? "private",
      name: settings.name ?? "",
      category: settings.category ?? currentTradingMode,
      stopLoss: settings.stopLoss?.toString() ?? "",
      target: settings.target?.toString() ?? "",
      trailingStopLoss: settings.trailingStopLoss?.toString() ?? "",
      orderType: settings.orderType ?? "market",
      limit: settings.limit?.toString() ?? "",
      orderTimeout: settings.orderTimeout?.toString() ?? "",
      autoIncreament: settings.autoIncreament ?? false,
      checkBenchmark: settings.checkBenchmark ?? false,
      sentimentAnalysis: settings.sentimentAnalysis ?? false,
      strikeDiff: settings.strikeDiff ?? "0",
      maxCapitalUsage: settings.maxCapitalUsage?.toString() as any ?? undefined,
      tradeCooldown: settings.tradeCooldown?.toString() as any ?? undefined,
      orderCategory: settings.orderCategory ?? "normal",
      tradeType: settings.tradeType ?? undefined,
      leverage: settings.leverage?.toString() ?? "",
      maxOrderAmount: settings.maxOrderAmount?.toString() ?? "",
      useLeader: settings.useLeader ?? false,
      leader: settings.leader ?? undefined,
      leaderStrategyIds: settings.leaderStrategyIds ?? [],
    });
  };

  const handleCancelEdit = () => {
    setEditingSettings(null);
    form.reset();
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Order Settings Form */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={cardVariants}
      >
        <Card className="border-transparent   ">
          <CardHeader>
            <div className="flex items-center space-x-3">
              {editingSettings && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCancelEdit}
                  className="rounded-full"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <CardTitle className="text-xl font-semibold">
                {editingSettings ? "Edit Configuration" : "Create New Configuration"}
              </CardTitle>
            </div>
            <CardDescription>
              {editingSettings
                ? "Update your existing order settings"
                : "Define parameters for your trading strategy"}
            </CardDescription>
          </CardHeader>
          <Separator className="mb-6" />
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(
                onSubmit,
                (errors) => {
                  console.error("❌ Zod validation errors:", errors);
                  toast({
                    title: "Validation Error",
                    description: "Please fix the highlighted fields.",
                    variant: "destructive",
                  });
                }
              )} className="space-y-8">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center">
                        Configuration Name
                        <Tooltip delayDuration={200}>
                          <TooltipTrigger asChild>
                            <Info className="h-4 w-4 ml-2 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p>Give your configuration a descriptive name</p>
                          </TooltipContent>
                        </Tooltip>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g., Conservative Setup"
                          {...field}
                          className="focus-visible:ring-2 focus-visible:ring-primary/50"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {
                  user?.role === "admin" ? (<FormField
                    control={form.control}
                    name="settingType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Privacy Type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="focus:ring-2 focus:ring-primary/50">
                              <SelectValue placeholder="Select Trade Type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="common" className="focus:bg-primary/10">
                              Common
                            </SelectItem>
                            <SelectItem value="private" className="focus:bg-primary/10">
                              Private
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />) : null
                }


                <motion.div
                  className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border-2 border-indigo-200"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="h-5 w-5 text-indigo-600" />
                    <h4 className="text-lg font-semibold text-indigo-900">Leader Following</h4>
                  </div>

                  <FormField
                    control={form.control}
                    name="useLeader"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 bg-white rounded-lg border border-indigo-200 mb-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="border-2 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm font-semibold text-slate-900">
                            Enable Leader Following
                          </FormLabel>
                          <p className="text-sm text-muted-foreground">
                            Follow a leader coin and apply leader strategies to your trades
                          </p>
                        </div>
                      </FormItem>
                    )}
                  />

                  {useLeader && (
                    <motion.div
                      className="space-y-4"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <FormField
                        control={form.control}
                        name="leader"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center text-sm font-semibold">
                              Select Leader
                              <Tooltip delayDuration={200}>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 ml-2 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>Choose a leader coin to follow for trading signals</p>
                                </TooltipContent>
                              </Tooltip>
                            </FormLabel>



                            <Select
                              value={field.value?._id || ""}
                              onValueChange={(value) => {
                                const selectedLeader = leaders?.find((l) => l._id === value);
                                field.onChange(selectedLeader ?? null);
                              }}
                            >
                              <FormControl>
                                <SelectTrigger className="bg-white border-indigo-200 focus:ring-2 focus:ring-indigo-500">
                                  <SelectValue placeholder="Select a leader" />
                                </SelectTrigger>
                              </FormControl>

                              <SelectContent className="max-h-60">
                                {leaders?.map((leader) => (
                                  <SelectItem
                                    key={leader._id}
                                    value={leader._id!}
                                    className="focus:bg-indigo-50"
                                  >
                                    {"symbol" in leader ? leader.symbol : leader.underlying_symbol}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="leaderStrategyIds"
                        render={({ field }) => {
                          const selectedStrategies = (field.value as string[]) || [];
                          return (
                            <FormItem>
                              <FormLabel className="flex items-center text-sm font-semibold">
                                Leader Strategies
                                <Tooltip delayDuration={200}>
                                  <TooltipTrigger asChild>
                                    <Info className="h-4 w-4 ml-2 text-muted-foreground" />
                                  </TooltipTrigger>
                                  <TooltipContent side="right">
                                    <p>Select multiple strategies to apply when following the leader</p>
                                  </TooltipContent>
                                </Tooltip>
                              </FormLabel>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    className="w-full justify-between bg-white border-indigo-200 hover:border-indigo-300 focus:ring-2 focus:ring-indigo-500"
                                  >
                                    <span className="text-left text-sm truncate">
                                      {selectedStrategies.length > 0
                                        ? strategies
                                          ?.filter((s) => selectedStrategies.includes(s.id ?? ""))
                                          .map((s) => s.function_name)
                                          .join(", ") || "Select Leader Strategies"
                                        : "Select Leader Strategies"}
                                    </span>
                                    <Check className="ml-2 h-4 w-4 opacity-50" />
                                  </Button>
                                </PopoverTrigger>

                                <PopoverContent className="w-full p-3 max-h-72 overflow-y-auto bg-white rounded-xl shadow-xl border-0">
                                  <div className="space-y-2">
                                    {strategies && strategies.length > 0 ? (
                                      strategies.map((strategy) => {
                                        const checked = selectedStrategies.includes(strategy.id ?? "");
                                        return (
                                          <div
                                            key={strategy.id}
                                            className="flex items-center space-x-3 p-3 rounded-lg hover:bg-indigo-50 cursor-pointer border border-transparent hover:border-indigo-200 transition-all"
                                            onClick={() => {
                                              if (checked) {
                                                field.onChange(
                                                  selectedStrategies.filter((id) => id !== strategy.id)
                                                );
                                              } else {
                                                field.onChange([...selectedStrategies, strategy.id ?? ""]);
                                              }
                                            }}
                                          >
                                            <Checkbox
                                              checked={checked}
                                              className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                                            />
                                            <span className="font-medium text-gray-700 text-sm">
                                              {strategy.function_name}
                                            </span>
                                          </div>
                                        );
                                      })
                                    ) : (
                                      <p className="text-sm text-muted-foreground text-center py-4">
                                        No leader strategies available
                                      </p>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />
                    </motion.div>
                  )}
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Risk Management */}
                  <motion.div
                    className="space-y-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 }}
                  >
                    <h4 className="text-lg font-semibold text-foreground flex items-center">
                      <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                      Risk Management
                    </h4>

                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="stopLoss"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Stop Loss (%)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.1"
                                placeholder="5"
                                {...field}
                                className="focus-visible:ring-2 focus-visible:ring-primary/50"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="target"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Take Profit (%)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.1"
                                placeholder="10"
                                {...field}
                                className="focus-visible:ring-2 focus-visible:ring-primary/50"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="trailingStopLoss"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center">
                              Trailing Stop Loss (%)
                              <Tooltip delayDuration={200}>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 ml-2 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>Automatically adjusts stop loss as price moves favorably</p>
                                </TooltipContent>
                              </Tooltip>
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.1"
                                placeholder="1"
                                {...field}
                                className="focus-visible:ring-2 focus-visible:ring-primary/50"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Execution Settings */}
                    <div className="space-y-6">
                      <h4 className="text-lg font-semibold text-foreground flex items-center">
                        <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                        Execution Settings
                      </h4>

                      <div className="space-y-4">

                        <FormField
                          control={form.control}
                          name="limit"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center">
                                Slippage Tolerance (%)
                                <Tooltip delayDuration={200}>
                                  <TooltipTrigger asChild>
                                    <Info className="h-4 w-4 ml-2 text-muted-foreground" />
                                  </TooltipTrigger>
                                  <TooltipContent side="right">
                                    <p>Maximum price slippage allowed for order execution</p>
                                  </TooltipContent>
                                </Tooltip>
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.5"
                                  {...field}
                                  className="focus-visible:ring-2 focus-visible:ring-primary/50"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="orderTimeout"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Order Timeout (seconds)</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  placeholder="60"
                                  {...field}
                                  className="focus-visible:ring-2 focus-visible:ring-primary/50"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />


                        <FormField
                          control={form.control}
                          name="tradeCooldown"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center">
                                Trade Cooldown (minutes)
                                <Tooltip delayDuration={200}>
                                  <TooltipTrigger asChild>
                                    <Info className="h-4 w-4 ml-2 text-muted-foreground" />
                                  </TooltipTrigger>
                                  <TooltipContent side="right">
                                    <p>
                                      Prevents placing another trade in the same symbol for a specified time period after exiting a trade.
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger className="focus:ring-2 focus:ring-primary/50">
                                    <SelectValue placeholder="Select Cooldown Time" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {["0", "15", "30", "45", "60", "75", "90", "105", "120"].map((value) => (
                                    <SelectItem
                                      key={value}
                                      value={value}
                                      className="focus:bg-primary/10"
                                    >
                                      {value === "0" ? "No Cooldown" : `${value} min`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="autoIncreament"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 bg-muted/50 rounded-lg">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  className="border-2 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>Auto Increment Position</FormLabel>
                                <p className="text-sm text-muted-foreground">
                                  Automatically increase position size on favorable moves
                                </p>
                              </div>
                            </FormItem>
                          )}
                        />

                        {currentTradingMode === "crypto" && (
                          <FormField
                            control={form.control}
                            name="leverage"
                            render={({ field }) => (
                              <FormItem className=" bg-muted/30">
                                <FormLabel className="text-sm font-medium">Leverage (5x–125x)</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value}>
                                  <FormControl>
                                    <SelectTrigger className="focus:ring-2 focus:ring-primary/50 shadow-sm">
                                      <SelectValue placeholder="Select Leverage" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent className="max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-muted-foreground/50 scrollbar-track-transparent">
                                    {Array.from({ length: 25 }, (_, i) => (i + 1) * 5).map(val => (
                                      <SelectItem
                                        key={val}
                                        value={String(val)}
                                        className="hover:bg-muted/40 px-3 py-2 rounded-md transition-colors"
                                      >
                                        {val}x
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}
                        <FormField
                          control={form.control}
                          name="maxOrderAmount"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                Max Amount per Order ({currentTradingMode === "crypto" ? "USDT" : "INR"})
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder={currentTradingMode === "crypto" ? "e.g., 1000 USDT" : "e.g., 5000 INR"}
                                  {...field}
                                  className="focus-visible:ring-2 focus-visible:ring-primary/50"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </motion.div>

                  {/* Order Settings */}
                  <motion.div
                    className="space-y-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <h4 className="text-lg font-semibold text-foreground flex items-center">
                      <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                      Order Settings
                    </h4>

                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="orderType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Order Type</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className="focus:ring-2 focus:ring-primary/50">
                                  <SelectValue placeholder="Select Order Type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="market" className="focus:bg-primary/10">
                                  Market Order
                                </SelectItem>
                                <SelectItem value="limit" className="focus:bg-primary/10">
                                  Limit Order
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="orderCategory"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Order Category</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className="focus:ring-2 focus:ring-primary/50">
                                  <SelectValue placeholder="Select Order Category" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="normal" className="focus:bg-primary/10">
                                  Normal
                                </SelectItem>
                                <SelectItem value="derivative" className="focus:bg-primary/10">
                                  Derivative
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="tradeType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Trade Type</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className="focus:ring-2 focus:ring-primary/50">
                                  <SelectValue placeholder="Select Trade Type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="delivery" className="focus:bg-primary/10">
                                  Delivery
                                </SelectItem>
                                <SelectItem value="intraday" className="focus:bg-primary/10">
                                  Intraday
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="maxCapitalUsage"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Max Capital Usage</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className="focus:ring-2 focus:ring-primary/50">
                                  <SelectValue placeholder="Select % Usage" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {[...Array(11)].map((_, i) => {
                                  const value = 50 + i * 5;
                                  return (
                                    <SelectItem
                                      key={value}
                                      value={String(value)}
                                      className="focus:bg-primary/10"
                                    >
                                      {value}%
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="strikeDiff"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Strike Difference</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className="focus:ring-2 focus:ring-primary/50">
                                  <SelectValue placeholder="Select Strike Difference" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {Array.from({ length: 11 }, (_, i) => i - 5).map((value) => (
                                  <SelectItem
                                    key={value}
                                    value={String(value)}
                                    className="focus:bg-primary/10"
                                  >
                                    {value < 0 ? `${value} (ITM)` : value > 0 ? `+${value} (OTM)` : "0 (ATM)"}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />


                      <FormField
                        control={form.control}
                        name="checkBenchmark"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 bg-muted/50 rounded-lg">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                className="border-2 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>Include Benchmark</FormLabel>
                              <p className="text-sm text-muted-foreground">
                                Include benchmark in every trade to add a check before order placement
                              </p>
                            </div>
                          </FormItem>
                        )}
                      />


                      <FormField
                        control={form.control}
                        name="sentimentAnalysis"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 bg-muted/50 rounded-lg">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                className="border-2 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>Enable Sentiment Analysis</FormLabel>
                              <p className="text-sm text-muted-foreground">
                                Use sentiment analysis signals before placing trades
                              </p>
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>
                  </motion.div>
                </div>

                <motion.div
                  className="flex space-x-3 pt-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="shadow-md hover:shadow-lg transition-shadow"
                  >
                    {createMutation.isPending || updateMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    {editingSettings ? "Update Configuration" : "Save Configuration"}
                  </Button>
                  {editingSettings && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancelEdit}
                      className="hover:bg-muted/50"
                    >
                      Cancel
                    </Button>
                  )}
                </motion.div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </motion.div>

      {/* Existing Order Settings */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={cardVariants}
        transition={{ delay: 0.2 }}
      >
        <Card className="border-transparent shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl font-semibold">
              Saved Configurations
            </CardTitle>
            <CardDescription>
              Your existing order setting configurations
            </CardDescription>
          </CardHeader>
          <Separator className="mb-6" />
          <CardContent>
            {orderSettings && orderSettings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence>
                  {orderSettings.map((settings: any, index: number) => (
                    <motion.div
                      key={settings.id}
                      custom={index}
                      initial="hidden"
                      animate="visible"
                      variants={listItemVariants}
                      exit={{ opacity: 0, scale: 0.9 }}
                      layout
                    >
                      <Card className="border-muted hover:border-primary/50 transition-colors group">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-semibold text-lg group-hover:text-primary transition-colors">
                              {settings.name}
                            </h4>

                            <Badge
                              variant="outline"
                              className="border-primary/20 bg-primary/10 text-primary"
                            >
                              {settings.category}
                            </Badge>


                            <div className="flex space-x-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(settings)}
                                className="rounded-full hover:bg-primary/10"
                              >
                                <Edit className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteMutation.mutate(settings.id)}
                                disabled={deleteMutation.isPending}
                                className="rounded-full hover:bg-destructive/10"
                              >
                                {deleteMutation.isPending ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4 text-muted-foreground group-hover:text-destructive transition-colors" />
                                )}
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-3 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Stop Loss:</span>
                              <span className="font-medium">
                                {settings.stopLoss}%
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Take Profit:</span>
                              <span className="font-medium">
                                {settings.target}%
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Order Type:</span>
                              <Badge
                                variant="outline"
                                className="border-primary/20 bg-primary/10 text-primary"
                              >
                                {settings.orderType}
                              </Badge>
                            </div>
                          </div>

                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-center py-12 space-y-4"
              >
                <div className="text-muted-foreground">
                  No order settings configured yet
                </div>
                <Button
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/10"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  Create your first configuration
                </Button>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}